// Discord OAuth2 Configuration
const DISCORD_CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID || "YOUR_CLIENT_ID_HERE";
const DISCORD_REDIRECT_URI = process.env.REACT_APP_DISCORD_REDIRECT_URI || "http://localhost:3000/auth/callback";
// OAuth2 URLs
const DISCORD_OAUTH_URL = "https://discord.com/api/oauth2/authorize";

class DiscordAuthService {
  constructor() {
    this.user = null;
    this.token = null;
    this.loadFromStorage();
  }

  // Carrega dados do localStorage
  loadFromStorage() {
    const storedUser = localStorage.getItem("discord_user");
    const storedToken = localStorage.getItem("discord_token");

    if (storedUser && storedToken) {
      this.user = JSON.parse(storedUser);
      this.token = storedToken;
    }
  }

  // Salva dados no localStorage
  saveToStorage() {
    if (this.user && this.token) {
      localStorage.setItem("discord_user", JSON.stringify(this.user));
      localStorage.setItem("discord_token", this.token);
    }
  }

  // Limpa dados do localStorage
  clearStorage() {
    localStorage.removeItem("discord_user");
    localStorage.removeItem("discord_token");
    this.user = null;
    this.token = null;
  }

  // Inicia o fluxo de autenticação OAuth2
  login() {
    const params = new URLSearchParams({
      client_id: DISCORD_CLIENT_ID,
      redirect_uri: DISCORD_REDIRECT_URI,
      response_type: "code",
      scope: "identify email guilds guilds.join",
    });

    window.location.href = `${DISCORD_OAUTH_URL}?${params.toString()}`;
  }

  // Troca o código por um token de acesso via backend
  async exchangeCode(code) {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      console.log("🔄 Trocando código por token via backend...");

      const response = await fetch(`${BACKEND_URL}/api/auth/discord`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to exchange code for token");
      }

      const data = await response.json();
      this.token = data.access_token;
      this.user = data.user;
      this.saveToStorage();

      console.log("✅ Autenticação bem-sucedida!", this.user);

      return true;
    } catch (error) {
      console.error("❌ Erro ao trocar código:", error);
      return false;
    }
  }

  // Busca informações do usuário autenticado e valida o token
  async fetchUser() {
    if (!this.token) return null;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      // Validar o token com o NOSSO backend primeiro (que por sua vez valida com Discord)
      const response = await fetch(`${BACKEND_URL}/api/auth/verify`, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn("⚠️ Sessão expirada. Limpando dados locais...");
          this.clearStorage();
        }

        return null;
      }

      const data = await response.json();

      // Se o backend renovou o token silenciosamente, atualizamos aqui também
      if (data.refreshed && data.access_token) {
        console.log("♻️ Token renovado automaticamente!");

        this.token = data.access_token;
        this.saveToStorage();
      }

      this.user = data.user;
      this.saveToStorage();

      return this.user;
    } catch (error) {
      console.error("Error fetching user:", error);
      // Não limpamos no erro de rede (500 ou offline), apenas no 401
      return null;
    }
  }

  // Faz logout
  logout() {
    this.clearStorage();
    window.location.href = "/";
  }

  // Verifica se o usuário está autenticado
  isAuthenticated() {
    return this.user !== null && this.token !== null;
  }

  // Retorna o usuário atual
  getUser() {
    return this.user;
  }

  // Retorna o avatar do usuário
  getUserAvatar() {
    if (!this.user) return null;

    if (this.user.avatar) {
      return `https://cdn.discordapp.com/avatars/${this.user.id}/${this.user.avatar}.png`;
    }

    // Avatar padrão
    return `https://cdn.discordapp.com/embed/avatars/${parseInt(this.user.discriminator) % 5}.png`;
  }

  // Retorna o nome de usuário formatado
  getUserTag() {
    if (!this.user) return null;
    return `${this.user.username}#${this.user.discriminator}`;
  }

  // Retorna o token de acesso
  getAccessToken() {
    return this.token;
  }

  // Helper para chamadas ao backend que cuida da renovação silenciosa
  async backendFetch(endpoint, options = {}) {
    if (!this.token) return null;

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    const url = endpoint.startsWith("http") ? endpoint : `${BACKEND_URL}${endpoint}`;

    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${this.token}`,
    };

    try {
      const response = await fetch(url, { ...options, headers });

      // Capturar novo token se o backend gerou um via middleware
      const newToken = response.headers.get("X-New-Access-Token");
      if (newToken) {
        console.log("♻️ Token atualizado via request header!");

        this.token = newToken;
        this.saveToStorage();
      }

      return response;
    } catch (error) {
      console.error("Erro no backendFetch:", error);
      throw error;
    }
  }

  // Busca as guilds do usuário através do nosso backend
  async fetchGuilds() {
    try {
      const response = await this.backendFetch("/api/auth/guilds");
      if (!response || !response.ok) return null;

      const data = await response.json();
      return data.guilds;
    } catch (error) {
      console.error("Error fetching guilds:", error);
      return null;
    }
  }
}

// Exporta uma instância única (singleton)
const authService = new DiscordAuthService();
export default authService;
